import { type ComponentPropsWithRef, type FC } from 'react';

import { useTimeFormatter } from '@mint-lib/formatting-hooks';
import { isValid } from 'date-fns';

/**
 * Formatting options for time values.
 */ type TimeFormatOptions = {
  /**
   * The format to use for the time.
   *
   * Examples:
   *
   * | Format | en-US | en-GB |
   * |--------|--------|--------|
   * | `p`    | 11:00 PM | 23:00 |
   * | `pp`   | 11:00:00 PM | 23:00:00 |
   * | `ppp`  | 11:00:00 PM GMT+2 | 23:00:00 GMT+2 |
   * | `pppp` | 11:00:00 PM GMT+02:00 | 23:00:00 GMT+02:00 |
   */
  format?: 'p' | 'pp' | 'ppp' | 'pppp';
};

type StaticTimeSpanProps = ComponentPropsWithRef<'span'> & {
  date: number | string | Date;
  options?: TimeFormatOptions;
};

/**
 * Displays a static formatted time string.
 */
const StaticTimeSpan: FC<StaticTimeSpanProps> = ({
  date,
  options,
  ...spanProps
}) => {
  const formatter = useTimeFormatter({ options });
  const parsedDate = new Date(date);

  if (!isValid(parsedDate)) {
    return null;
  }

  return <span {...spanProps}>{formatter.format(parsedDate)}</span>;
};

export default StaticTimeSpan;
